import type * as THREE from "three";

// Cloud shadow on the low-poly surface (decision 0088): broken light, soft shapes of shade lying on the ground and on
// everything standing on it, drifting with the game's clock. A value-noise field over the world's x and z (projected
// straight down, so a tree and the ground under it share their shade), thresholded at the coverage and softened at
// the edge; where it is in shade the key light (the surface's direct light: the sun by day, the moon by night) is
// taken away by `strength`, so a shaded patch keeps only its fill, as under a real cloud. Arithmetic only: no texture,
// no pass, no draw. The shader skips it under a uniform test while the strength is 0, so a game that never sets it
// draws exactly as before. The game owns every value (`DayKey.cloudShadow`, `WeatherLook.cloudShadow`,
// `LowPolySceneLook.cloudShadow`); the package owns the noise, the coverage's meaning and the drift.

/** A cloud shadow's shape: how deep, how much of the ground, how big, how soft. */
export interface CloudShadow {
  /** The share of the key light a full shadow takes away, 0 (none, as at night) to 1 (only the fill left). */
  strength: number;
  /** About the share of the ground in shade, 0 to 1 (measured on the noise; see {@link cloudThreshold}). */
  coverage: number;
  /** The shapes' size (m): the noise's cell, so one shape is about this across. Tens of metres for a valley. */
  scale: number;
  /** The edge's width in the noise's units: 0.02 crisp, 0.1 soft (the default, {@link CLOUD_SOFTNESS}), 0.3 a blur. */
  softness?: number;
}

/** A cloud shadow at one moment: its shape and how far the field has drifted (m, along x and z) since the clock's
 *  start. */
export interface CloudShadowFrame {
  strength: number;
  coverage: number;
  scale: number;
  softness: number;
  offset: [number, number];
}

/** The edge's width when a shape doesn't give one. */
export const CLOUD_SOFTNESS = 0.1;

/** No cloud shadow (the strength 0: the shader skips it). */
export const NO_CLOUD_SHADOW: Readonly<CloudShadowFrame> = Object.freeze({ strength: 0, coverage: 0, scale: 1, softness: CLOUD_SOFTNESS, offset: [0, 0] as [number, number] });

/** The noise repeats every this many cells along x and z (its hash's period); the drift is wrapped by it, so the
 *  shader's coordinates stay small however long the game runs. */
export const CLOUD_PERIOD = 10000;

const fract = (x: number): number => x - Math.floor(x);

/** The noise's hash: the GLSL's `lpCloudH`, cell (x, y) to 0..1. */
function hash(x: number, y: number): number {
  let a = fract(x * 0.1031);
  let b = fract(y * 0.1031);
  let c = fract(x * 0.1031);
  const d = a * (b + 33.33) + b * (c + 33.33) + c * (a + 33.33);
  a += d;
  b += d;
  c += d;
  return fract((a + b) * c);
}

function valueNoise(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  let fx = x - ix;
  let fy = y - iy;
  fx = fx * fx * (3 - 2 * fx);
  fy = fy * fy * (3 - 2 * fy);
  const a = hash(ix, iy);
  const b = hash(ix + 1, iy);
  const c = hash(ix, iy + 1);
  const e = hash(ix + 1, iy + 1);
  const lo = a + (b - a) * fx;
  const hi = c + (e - c) * fx;
  return lo + (hi - lo) * fy;
}

/** The cloud field at (x, y) in noise cells, 0..1 (three octaves of value noise; the GLSL's `lpCloudF`). Its median
 *  is about 0.5. */
export function cloudNoise(x: number, y: number): number {
  return (0.5 * valueNoise(x, y) + 0.25 * valueNoise(x * 2 + 17, y * 2 + 9) + 0.125 * valueNoise(x * 4 - 31, y * 4 + 23)) / 0.875;
}

/** The field's quantiles at coverage 0, 0.05, … 1 (measured over a million samples of {@link cloudNoise}): the field
 *  is under `CLOUD_QUANTILES[i]` on about i × 5 % of the ground. */
export const CLOUD_QUANTILES: readonly number[] = [0.04, 0.2701, 0.3147, 0.3467, 0.3733, 0.3973, 0.4193, 0.4401, 0.4598, 0.4795, 0.4988, 0.5184, 0.5381, 0.5584, 0.5795, 0.6016, 0.6255, 0.6525, 0.6854, 0.7309, 0.9663];

/** The field's value under which a point is in shade, for a coverage (0 none, 1 everywhere; between, about that
 *  share of the ground). */
export function cloudThreshold(coverage: number): number {
  if (!(coverage > 0)) return -1;
  if (coverage >= 1) return 2;
  const f = coverage * (CLOUD_QUANTILES.length - 1);
  const i = Math.floor(f);
  const a = CLOUD_QUANTILES[i]!;
  const b = CLOUD_QUANTILES[Math.min(i + 1, CLOUD_QUANTILES.length - 1)]!;
  return a + (b - a) * (f - i);
}

/** The shader's uniform values for a frame: `cloud` = (strength, threshold, softness, 1 / scale), `at` = the drift in
 *  noise cells (negated: the field moves with the drift), wrapped by {@link CLOUD_PERIOD}. */
export function cloudUniformValues(c: Readonly<CloudShadowFrame>): { cloud: [number, number, number, number]; at: [number, number] } {
  const strength = c.coverage > 0 ? Math.min(1, Math.max(0, c.strength)) : 0;
  const inv = 1 / Math.max(1e-3, c.scale);
  const wrap = (v: number): number => {
    const w = v % CLOUD_PERIOD;
    return w < 0 ? w + CLOUD_PERIOD : w;
  };
  return { cloud: [strength, cloudThreshold(c.coverage), Math.max(1e-4, c.softness), inv], at: [wrap(-c.offset[0] * inv), wrap(-c.offset[1] * inv)] };
}

const smoothstep = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// The shader's field in its own arithmetic (IMP-0025). The hash multiplies a cell's index by 0.1031, then amplifies
// the fraction about three thousand times (a dot with 33.33, then a product), so in a GPU's 32-bit floats a cell
// thousands of cells out hashes to a different value than in JavaScript's 64-bit ones: a twin in 64 bits agreed with
// the frame near the clock's start and drifted away from it as the field drifted (98 % of the pixels on day 1, 61 % on
// day 40 in spike 002's lab). These mirror the GLSL step by step, each result rounded to 32 bits.
const f32 = Math.fround;
const K32 = f32(0.1031);
const C32 = f32(33.33);
const fract32 = (x: number): number => f32(x - Math.floor(x));
const mix32 = (a: number, b: number, t: number): number => f32(f32(a * f32(1 - t)) + f32(b * t));

/** The GLSL's `lpCloudH` in 32-bit floats. */
function hash32(x: number, y: number): number {
  let a = fract32(f32(x * K32));
  let b = fract32(f32(y * K32));
  let c = fract32(f32(x * K32));
  const d = f32(f32(f32(a * f32(b + C32)) + f32(b * f32(c + C32))) + f32(c * f32(a + C32)));
  a = f32(a + d);
  b = f32(b + d);
  c = f32(c + d);
  return fract32(f32(f32(a + b) * c));
}

/** The GLSL's `lpCloudN` in 32-bit floats. */
function valueNoise32(x: number, y: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  let fx = fract32(x);
  let fy = fract32(y);
  fx = f32(f32(fx * fx) * f32(3 - f32(2 * fx)));
  fy = f32(f32(fy * fy) * f32(3 - f32(2 * fy)));
  return mix32(mix32(hash32(ix, iy), hash32(ix + 1, iy), fx), mix32(hash32(ix, iy + 1), hash32(ix + 1, iy + 1), fx), fy);
}

/** The cloud field at (x, y) in noise cells as the shader computes it, in 32-bit floats (`cloudNoise` is the same
 *  field in 64 bits: equal near the origin, apart thousands of cells out, where the hash's rounding takes over). */
export function cloudNoiseF32(x: number, y: number): number {
  const px = f32(x);
  const py = f32(y);
  const n1 = valueNoise32(px, py);
  const n2 = valueNoise32(f32(f32(px * 2) + 17), f32(f32(py * 2) + 9));
  const n3 = valueNoise32(f32(f32(px * 4) - 31), f32(f32(py * 4) + 23));
  return f32(f32(f32(f32(0.5 * n1) + f32(0.25 * n2)) + f32(0.125 * n3)) / f32(0.875));
}

/** Planted defect for the twin's agreement with the frame: `twin64` reads the field in 64-bit floats, as the twin did
 *  before IMP-0025 (the frame's far-drifted shadows disagree). */
export const CLOUD_FAULTS = { twin64: false };

/** The share of the key light the cloud shadow takes away at world (x, z), 0 to its strength: the shader's sum, on
 *  the CPU (for tests, and for anything that wants to know whether a point is in shade), in the shader's 32-bit
 *  arithmetic so it agrees with the drawn frame however far the field has drifted (IMP-0025). */
export function cloudShadowAt(c: Readonly<CloudShadowFrame>, x: number, z: number): number {
  const v = cloudUniformValues(c);
  const [strength, th, soft, inv] = v.cloud;
  if (!(strength > 0)) return 0;
  // the shader's coordinate: vLpWorld.xz * uLpCloud.w + uLpCloudAt, its uniforms 32-bit
  const n = CLOUD_FAULTS.twin64 ? cloudNoise(x * inv + v.at[0], z * inv + v.at[1]) : cloudNoiseF32(f32(f32(f32(x) * f32(inv)) + f32(v.at[0])), f32(f32(f32(z) * f32(inv)) + f32(v.at[1])));
  return strength * (1 - smoothstep(th - soft, th + soft, n));
}

/** The cloud shadow's uniforms on the low-poly surface. */
export interface CloudShadowUniforms {
  /** (strength, threshold, softness, 1 / scale); strength 0 skips the shadow. */
  uLpCloud: THREE.IUniform<THREE.Vector4>;
  /** The drift, in noise cells. */
  uLpCloudAt: THREE.IUniform<THREE.Vector2>;
}

/** Write a frame's cloud shadow into its uniforms (a time-of-day driver every frame; `setLowPolyUniforms`). */
export function setCloudShadow(u: CloudShadowUniforms, c: Readonly<CloudShadowFrame>): void {
  const v = cloudUniformValues(c);
  u.uLpCloud.value.set(v.cloud[0], v.cloud[1], v.cloud[2], v.cloud[3]);
  u.uLpCloudAt.value.set(v.at[0], v.at[1]);
}

/** The cloud shadow's GLSL: `decl` before the fragment shader's main (the uniforms and the noise), `body` in the
 *  surface's patch where `outgoingLight` and three's `reflectedLight` are both in scope (it takes the shaded share of
 *  the direct light away). */
export const CLOUD_GLSL = {
  decl: /* glsl */ `uniform vec4 uLpCloud;
uniform vec2 uLpCloudAt;
float lpCloudH(vec2 p) { vec3 q = fract(p.xyx * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
float lpCloudN(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(lpCloudH(i), lpCloudH(i + vec2(1.0, 0.0)), f.x), mix(lpCloudH(i + vec2(0.0, 1.0)), lpCloudH(i + vec2(1.0, 1.0)), f.x), f.y);
}
float lpCloudF(vec2 p) { return (0.5 * lpCloudN(p) + 0.25 * lpCloudN(p * 2.0 + vec2(17.0, 9.0)) + 0.125 * lpCloudN(p * 4.0 + vec2(-31.0, 23.0))) / 0.875; }
`,
  body: /* glsl */ `
  if (uLpCloud.x > 0.0) {
    // cloud shadow: the shaded share of the direct (key) light taken away; the fill stays
    float lpC = lpCloudF(vLpWorld.xz * uLpCloud.w + uLpCloudAt);
    float lpS = uLpCloud.x * (1.0 - smoothstep(uLpCloud.y - uLpCloud.z, uLpCloud.y + uLpCloud.z, lpC));
    outgoingLight -= reflectedLight.directDiffuse * lpS;
  }
`,
};
